import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import type { IntlService } from 'ember-intl';
import type { CompanyLpnSettingExtension as CompanyLpnSetting } from 'tio-data/data/resources/company-lpn-setting.ext';
import type { LpnPageExtension as LpnPage } from 'tio-data/data/resources/lpn-page.ext';

/** The fields a school card shows, after the company's override is merged over the page. */
export type LpnCardView = Pick<
  LpnPage,
  | 'badgeCappedTuition'
  | 'badgeCappedTuitionRange'
  | 'badgeDiscount'
  | 'badgeFamilySavings'
  | 'badgeFeesWaived'
  | 'costPerCredit'
  | 'costPerCreditRange'
  | 'discountPercent'
  | 'discountPercentRange'
  | 'employerRecommended'
  | 'format'
  | 'imageUrl'
  | 'lowTuition'
  | 'partTuitionWaiver'
  | 'programs'
  | 'providedBy'
  | 'saveDollarAmount'
  | 'saveDollarAmountRange'
  | 'saveDollarAmountValue'
  | 'schoolName'
  | 'url'
  | 'useCostPerCreditRange'
  | 'useDiscountPercentRange'
  | 'useSaveDollarAmountRange'
>;

/**
 * The company's capped-tuition badge for a page. An explicit badge override wins; otherwise a global
 * page takes the company's capped-tuition toggle, which tio-admin writes as `cappedTuition`. A
 * company's own page has no global value to toggle, so it keeps its own badge.
 */
function effectiveCappedTuition(page: LpnPage, override: CompanyLpnSetting | undefined) {
  const toggle = page.isCompanyCreated === true ? undefined : override?.cappedTuition;
  return override?.badgeCappedTuition ?? toggle ?? page.badgeCappedTuition;
}

/**
 * Merges the company's override over the global page; an override field left null inherits the page.
 *
 * @param override the viewing company's setting for this page, when it has one
 */
export function buildLpnCardView(
  page: LpnPage,
  override: CompanyLpnSetting | undefined
): LpnCardView {
  return {
    badgeCappedTuition: effectiveCappedTuition(page, override),
    badgeCappedTuitionRange: override?.badgeCappedTuitionRange ?? page.badgeCappedTuitionRange,
    badgeDiscount: override?.badgeDiscount ?? page.badgeDiscount,
    badgeFamilySavings: override?.badgeFamilySavings ?? page.badgeFamilySavings,
    badgeFeesWaived: override?.badgeFeesWaived ?? page.badgeFeesWaived,
    costPerCredit: override?.costPerCredit ?? page.costPerCredit,
    costPerCreditRange: override?.costPerCreditRange ?? page.costPerCreditRange,
    discountPercent:
      override?.discountPercent === null || override?.discountPercent === undefined
        ? page.discountPercent
        : String(override.discountPercent),
    discountPercentRange: override?.discountPercentRange ?? page.discountPercentRange,
    employerRecommended: override?.employerRecommended ?? page.employerRecommended,
    format: override?.format ?? page.format,
    imageUrl: override?.imageUrl ?? page.imageUrl,
    lowTuition: override?.lowTuition ?? page.lowTuition,
    partTuitionWaiver: override?.partTuitionWaiver ?? page.partTuitionWaiver,
    programs: override?.programs ?? page.programs,
    providedBy: override?.providedBy ?? page.providedBy,
    saveDollarAmount: override?.saveDollarAmount ?? page.saveDollarAmount,
    saveDollarAmountRange: override?.saveDollarAmountRange ?? page.saveDollarAmountRange,
    saveDollarAmountValue: override?.saveDollarAmountValue ?? page.saveDollarAmountValue,
    schoolName: page.schoolName,
    url: override?.url ?? page.url,
    useCostPerCreditRange: override?.useCostPerCreditRange ?? page.useCostPerCreditRange,
    useDiscountPercentRange: override?.useDiscountPercentRange ?? page.useDiscountPercentRange,
    useSaveDollarAmountRange: override?.useSaveDollarAmountRange ?? page.useSaveDollarAmountRange,
  };
}

/** A school's savings and feature labels, ready to render. */
export interface LpnBadges {
  /** The first of capped tuition, savings percent or savings amount; shown prominently. */
  topBadge: string | null;
  /** Every other label, excluding the top badge so it is not shown twice. */
  tags: string[];
}

/**
 * The translated labels a school card shows for its savings, features, formats and programs.
 */
export function lpnBadges(view: LpnCardView, intl: IntlService): LpnBadges {
  const save = intl.t('lpn.badges.save');

  let cappedTuition: string | null = null;
  if (view.badgeCappedTuition) {
    const range = (view.badgeCappedTuitionRange ?? '').trim();
    cappedTuition = range
      ? `${intl.t('lpn.badges.capped_tuition_at')} $${range}`
      : intl.t('lpn.badges.capped_tuition');
  }

  let savingsPercent: string | null = null;
  if (view.useDiscountPercentRange) savingsPercent = `${save} ${view.discountPercentRange}%`;
  else if (view.badgeDiscount) savingsPercent = `${save} ${view.discountPercent}%`;

  let savingsAmount: string | null = null;
  if (view.useSaveDollarAmountRange) savingsAmount = `${save} $${view.saveDollarAmountRange}`;
  else if (view.saveDollarAmount) savingsAmount = `${save} $${view.saveDollarAmountValue}`;

  let costPerCredit: string | null = null;
  const costLabel = intl.t('lpn.badges.cost_per_credit');
  if (view.useCostPerCreditRange) costPerCredit = `${costLabel}: $${view.costPerCreditRange}`;
  else if (view.costPerCredit?.length) costPerCredit = `${costLabel}: $${view.costPerCredit}`;

  const topBadge = cappedTuition ?? savingsPercent ?? savingsAmount ?? null;
  const tags = [
    cappedTuition,
    savingsPercent,
    savingsAmount,
    view.partTuitionWaiver ? intl.t('lpn.badges.part_tuition_waiver') : null,
    view.lowTuition ? intl.t('lpn.badges.low_tuition') : null,
    view.badgeFamilySavings ? intl.t('lpn.badges.family_eligible') : null,
    view.badgeFeesWaived ? intl.t('lpn.badges.no_fees') : null,
    costPerCredit,
    ...(view.format ?? []),
    ...(view.programs ?? []),
  ].filter((tag): tag is string => Boolean(tag) && tag !== topBadge);

  return { topBadge, tags };
}

interface DescriptionBlock {
  type?: string;
  data?: {
    text?: string;
    level?: string;
    style?: string;
    items?: (string | { content?: string })[];
  };
}

/**
 * The school's Editor.js description as HTML: the company's description when it has one,
 * otherwise the global page's. The blocks are authored by admins in tio-admin.
 *
 * @param override the viewing company's setting for this page, when it has one
 */
export function lpnDescriptionHtml(
  page: LpnPage,
  override: CompanyLpnSetting | undefined
): SafeString {
  const companyBlocks = (override?.description?.blocks ?? []) as DescriptionBlock[];
  const pageBlocks = (page.description?.blocks ?? []) as DescriptionBlock[];
  const blocks = companyBlocks.length > 0 ? companyBlocks : pageBlocks;

  const html = blocks.map((block) => {
    switch (block.type) {
      case 'paragraph':
        return `<p>${block.data?.text ?? ''}</p>`;
      case 'header': {
        const level = Math.min(6, Math.max(1, parseInt(block.data?.level ?? '', 10) || 3));
        return `<h${level}>${block.data?.text ?? ''}</h${level}>`;
      }
      case 'list': {
        const tag = block.data?.style === 'ordered' ? 'ol' : 'ul';
        const items = (block.data?.items ?? [])
          .map((item) => `<li>${typeof item === 'string' ? item : (item?.content ?? '')}</li>`)
          .join('');
        return `<${tag}>${items}</${tag}>`;
      }
      default:
        return block.data?.text ? `<p>${block.data.text}</p>` : '';
    }
  });
  return htmlSafe(html.join(''));
}
